import { useState, useEffect } from "react";

import { motion } from "framer-motion";
import Lottie from "lottie-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import lunovaPremium from "../assets/lunova-premium.json";
import verifyEmail from "../assets/verify-email.json";
import { useAuth } from "../contexts/AuthContext";
import { useGradient } from "../contexts/GradientContext";
import { fetchWeatherAndSetBackground } from "../utils/weatherBackground";

const Login = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [isUSA, setIsUSA] = useState(false);
  const [loading, setLoading] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [weatherType, setWeatherType] = useState("");
  const [weatherIcon, setWeatherIcon] = useState("");
  const [temperature, setTemperature] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState("");
  const [subscriptionType, setSubscriptionType] = useState("");
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);

  const { login, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentGradient } = useGradient();

  const preloadAndSetBackground = (url) => {
    if (!url) return;
    const img = new Image();

    img.onload = () => setBackgroundImage(url);
    img.onerror = () => {};
    img.src = url;
  };

  // Only bounce away from login once AuthContext confirms a live session.
  // A stale/expired localStorage token must not block a fresh login attempt.
  useEffect(() => {
    if (authLoading || !isAuthenticated) return;

    const currentParams = searchParams.toString();

    if (currentParams) {
      navigate(`/dashboard?${currentParams}`);
    } else {
      navigate("/dashboard");
    }
  }, [authLoading, isAuthenticated, navigate, searchParams]);

  useEffect(() => {
    const registerConfirmation = searchParams.get("registerConfirmation");
    const stripeConfirmation = searchParams.get("stripeConfirmation");
    const message = searchParams.get("message");
    const email = searchParams.get("email");
    const error = searchParams.get("error");

    if (
      stripeConfirmation &&
      ["trial", "monthly", "quarterly", "yearly"].includes(stripeConfirmation)
    ) {
      setSubscriptionType(stripeConfirmation);
      setShowConfirmationModal(true);
      window.history.replaceState(null, "", window.location.href);
    }

    if (registerConfirmation) {
      setShowRegistrationModal(true);
      window.history.replaceState(null, "", window.location.href);
    }

    if (message === "email-verified") {
      toast.success("Email verified successfully! Please log in.");
    } else if (message === "already-verified") {
      toast.info("Your email is already verified. Please log in.");
    }

    if (error === "missing-token") {
      toast.error("Verification link is invalid. Missing token.");
    } else if (error === "invalid-token") {
      toast.error("Invalid verification link.");
    } else if (error === "registration-not-found") {
      toast.error("Registration not found. Please sign up again.");
    } else if (error === "token-expired") {
      toast.error("Verification link has expired. Please request a new one.");
    } else if (error === "verification-failed") {
      toast.error("Email verification failed. Please try again.");
    }

    if (email && email?.includes("@")) {
      setFormData((prev) => ({ ...prev, email }));
    }

    if (message || error) {
      window.history.replaceState(null, "", window.location.href);
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    let mounted = true;
    const hasNavigator = typeof navigator !== "undefined";

    (async () => {
      await fetchWeatherAndSetBackground({
        setTemperature: (t) => mounted && setTemperature(t),
        setWeatherIcon: (i) => mounted && setWeatherIcon(i),
        setWeatherType: (s) => mounted && setWeatherType(s),
        setIsUSA: (b) => mounted && setIsUSA(b),
        setBackgroundUrl: (url) => mounted && preloadAndSetBackground(url),
        geolocator: hasNavigator ? navigator : undefined,
      });
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(formData.email, formData.password);

    if (result.success) {
      toast.success(`Welcome back ${result.firstName}!`);
      const redirect = searchParams.get("redirect");

      if (redirect) {
        navigate(decodeURIComponent(redirect));
      } else {
        navigate("/dashboard");
      }
    } else {
      toast.error(result.error);
    }

    setLoading(false);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6"
      style={{
        background: `linear-gradient(rgba(31, 41, 55, 0.5), rgba(31, 41, 55, 0.5)), ${currentGradient}`,
      }}
    >
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-5xl"
        initial={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.5 }}
      >
        {/* Main Card Container */}
        <div
          className="relative backdrop-blur-sm bg-gray-800/20 rounded-3xl overflow-hidden"
          style={{
            boxShadow:
              "0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 100px rgba(0, 0, 0, 0.3)",
            minHeight: "650px",
          }}
        >
          {/* Background Image - Full width */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${backgroundImage})`,
              filter: "brightness(0.9)",
            }}
          />

          {/* Gradient Overlay - from left to right */}
          <div
            className="absolute inset-0 z-10"
            style={{
              background:
                "linear-gradient(to right, rgba(31, 41, 55, 0.98), rgba(31, 41, 55, 0.9) 30%, rgba(31, 41, 55, 0.7) 50%, rgba(31, 41, 55, 0.3) 70%, transparent 90%)",
            }}
          />

          {/* Content Container */}
          <div className="relative flex z-20">
            {/* Left Side - Login Form */}
            <div
              className="relative px-6 py-12 sm:p-12 flex flex-col justify-center"
              style={{ width: "480px", minHeight: "650px" }}
            >
              {/* Logo - positioned higher and to the left */}
              <div className="absolute top-8 left-6 sm:left-10">
                <div
                  aria-hidden="true"
                  className="inline-flex items-center justify-center w-10 h-10"
                >
                  {!logoError ? (
                    <img
                      alt="Lunova Player Logo"
                      className="w-full h-full object-contain"
                      src="/logo.png"
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-purple-500 to-orange-500 rounded-lg" />
                  )}
                </div>
              </div>

              {/* Form Content */}
              <div className="mt-16 w-full max-w-sm">
                <div className="mb-8">
                  <p
                    className="text-gray-400 text-xs uppercase tracking-wider mb-4"
                    style={{
                      fontFamily: "Open Sans, sans-serif",
                      fontWeight: 400,
                    }}
                  >
                    Start for free
                  </p>
                  <h2
                    className="text-3xl font-bold text-white mb-3 whitespace-nowrap"
                    style={{
                      fontFamily: "Open Sans, sans-serif",
                      fontWeight: 700,
                    }}
                  >
                    Sign in to continue.
                  </h2>
                  <p
                    className="text-gray-400"
                    style={{
                      fontFamily: "Open Sans, sans-serif",
                      fontWeight: 400,
                    }}
                  >
                    Not Registered?{" "}
                    <button
                      aria-label="Create a new account"
                      className="text-blue-400 hover:text-blue-300 transition-colors"
                      type="button"
                      onClick={() => window.location.replace("/register")}
                    >
                      Create one
                    </button>
                  </p>
                </div>

                {/* Login Form */}
                <form
                  aria-label="Login form"
                  className="space-y-5"
                  onSubmit={handleSubmit}
                >
                  {/* Email Field */}
                  <div className="relative">
                    <input
                      required
                      aria-label="Email address"
                      aria-required="true"
                      autoComplete="email"
                      className="w-full px-4 py-3 pr-10 rounded-lg bg-gray-700/30 backdrop-blur-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-600 transition-all"
                      id="email-input"
                      name="email"
                      placeholder="Email"
                      style={{
                        fontFamily: "Open Sans, sans-serif",
                        fontWeight: 400,
                      }}
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                    <i className="fas fa-envelope absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                  </div>

                  {/* Password Field */}
                  <div className="relative">
                    <input
                      required
                      aria-label="Password"
                      aria-required="true"
                      autoComplete="current-password"
                      className="w-full px-4 py-3 pr-10 rounded-lg bg-gray-700/30 backdrop-blur-sm border border-blue-500/50 text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      id="password-input"
                      name="password"
                      placeholder="Password"
                      style={{
                        fontFamily: "Open Sans, sans-serif",
                        fontWeight: 400,
                      }}
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleChange}
                    />
                    <button
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      aria-pressed={showPassword}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <i
                        aria-hidden="true"
                        className={`fas fa-${showPassword ? "eye-slash" : "eye"}`}
                      />
                    </button>
                  </div>

                  {/* Remember Me and Forgot Password */}
                  <div className="flex items-center justify-between">
                    <label
                      className="flex items-center cursor-pointer"
                      htmlFor="remember-checkbox"
                    >
                      <input
                        aria-label="Remember me"
                        checked={formData.rememberMe}
                        className="w-4 h-4 rounded bg-gray-700/30 border-gray-600 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                        id="remember-checkbox"
                        name="rememberMe"
                        type="checkbox"
                        onChange={handleChange}
                      />
                      <span
                        className="ml-2 text-sm text-gray-400"
                        style={{
                          fontFamily: "Open Sans, sans-serif",
                          fontWeight: 400,
                        }}
                      >
                        Remember me
                      </span>
                    </label>
                    <button
                      aria-label="Go to forgot password page"
                      className="text-sm text-gray-400 hover:text-gray-300 transition-colors"
                      style={{
                        fontFamily: "Open Sans, sans-serif",
                        fontWeight: 400,
                      }}
                      type="button"
                      onClick={() =>
                        window.location.replace("/forgot-password")
                      }
                    >
                      Forgot password?
                    </button>
                  </div>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                    <button
                      aria-label="Go to registration page"
                      className="w-full py-3 px-6 rounded-full bg-gray-600/50 text-gray-300 text-center font-semibold hover:bg-gray-600/60 active:scale-95 transition-all duration-200 order-2 sm:order-1"
                      style={{
                        fontFamily: "Open Sans, sans-serif",
                        fontWeight: 600,
                      }}
                      type="button"
                      onClick={() => window.location.replace("/register")}
                    >
                      Create Account
                    </button>
                    <button
                      aria-busy={loading}
                      aria-label="Sign In"
                      className="w-full py-3 px-6 rounded-full bg-blue-500 text-white font-semibold hover:bg-blue-600 active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed order-1 sm:order-2 flex items-center justify-center"
                      disabled={loading}
                      style={{
                        fontFamily: "Open Sans, sans-serif",
                        fontWeight: 600,
                      }}
                      type="submit"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                          <span>Signing In...</span>
                        </>
                      ) : (
                        "Sign In"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Right Side - Empty space for image to show through */}
            <div className="flex-1" />
          </div>

          {/* Weather Info Overlays */}
          {temperature !== null && (
            <>
              {/* Temperature in top right */}
              <div className="absolute top-8 right-12 text-right">
                <div className="flex items-center space-x-2">
                  <i
                    className={`fas ${weatherIcon} text-xl text-white drop-shadow-lg`}
                  />
                  <p
                    className="text-2xl font-bold text-white drop-shadow-lg"
                    style={{ fontFamily: "Open Sans, sans-serif" }}
                  >
                    {temperature}°{isUSA ? "F" : "C"}
                  </p>
                </div>
              </div>

              {/* Weather description in bottom right */}
              <div className="absolute bottom-8 right-12 text-right">
                <p
                  className="text-white text-xs font-semibold drop-shadow-lg"
                  style={{ fontFamily: "Open Sans, sans-serif" }}
                >
                  <i className={`fas ${weatherIcon} mr-1 text-xs`} />
                  {weatherType}
                </p>
              </div>
            </>
          )}
        </div>

        {/* Terms and Privacy - Below the card */}
        <div className="mt-6 text-center">
          <p
            className="text-xs text-gray-400"
            style={{ fontFamily: "Open Sans, sans-serif", fontWeight: 400 }}
          >
            By signing in, you agree to our{" "}
            <a
              className="text-blue-400 hover:text-blue-300 transition-colors"
              href="https://lunova.app/dev/tos"
              rel="noopener noreferrer"
              target="_blank"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              className="text-blue-400 hover:text-blue-300 transition-colors"
              href="https://lunova.app/dev/privacy"
              rel="noopener noreferrer"
              target="_blank"
            >
              Privacy Policy
            </a>
          </p>
        </div>
      </motion.div>

      {/* Subscription Confirmation Modal */}
      {showConfirmationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            animate={{ opacity: 1 }}
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            onClick={() => setShowConfirmationModal(false)}
          />

          {/* Modal */}
          <motion.div
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden border border-gray-700/50"
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            <div className="absolute inset-0 flex items-center justify-center opacity-10">
              <Lottie
                animationData={lunovaPremium}
                autoplay={true}
                loop={true}
                style={{ width: "70%", height: "70%" }}
              />
            </div>

            {/* Modal Content */}
            <div className="relative p-8">
              {/* Success Checkmark */}
              <motion.div
                animate={{ scale: 1 }}
                className="mx-auto w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mb-6 shadow-lg shadow-green-500/25"
                initial={{ scale: 0 }}
                transition={{ delay: 0.1, type: "spring", damping: 15 }}
              >
                <i className="fas fa-check text-white text-3xl" />
              </motion.div>

              {/* Title */}
              <motion.h2
                animate={{ opacity: 1, y: 0 }}
                className="text-3xl font-bold text-white text-center mb-3"
                initial={{ opacity: 0, y: 10 }}
                style={{ fontFamily: "Open Sans, sans-serif" }}
                transition={{ delay: 0.2 }}
              >
                Welcome to Lunova Player!
              </motion.h2>

              {/* Subscription Success Message */}
              <motion.p
                animate={{ opacity: 1, y: 0 }}
                className="text-gray-300 text-center mb-6"
                initial={{ opacity: 0, y: 10 }}
                style={{ fontFamily: "Open Sans, sans-serif" }}
                transition={{ delay: 0.3 }}
              >
                {subscriptionType === "trial"
                  ? "Thank you for subscribing and starting your free trial!"
                  : subscriptionType === "monthly"
                    ? "Thank you for subscribing! Your monthly payment has been successfully processed."
                    : subscriptionType === "quarterly"
                      ? "Thank you for subscribing! Your quarterly payment has been successfully processed."
                      : subscriptionType === "yearly"
                        ? "Thank you for subscribing! Your yearly payment has been successfully processed."
                        : `Thank you for subscribing! Your ${subscriptionType} payment has been successfully processed.`}
              </motion.p>

              {/* Email Verification Notice */}
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="bg-blue-500/10 border border-blue-400/30 rounded-xl p-4 mb-6"
                initial={{ opacity: 0, y: 10 }}
                transition={{ delay: 0.4 }}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <i className="fas fa-envelope text-blue-400 text-xl" />
                  </div>
                  <div>
                    <h3
                      className="text-white font-semibold mb-1"
                      style={{ fontFamily: "Open Sans, sans-serif" }}
                    >
                      Please Verify Your Email
                    </h3>
                    <p
                      className="text-gray-300 text-sm"
                      style={{ fontFamily: "Open Sans, sans-serif" }}
                    >
                      Your registration is complete! Your account has been
                      successfully created. Please verify your email address to
                      activate your account and start managing your content.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Features List - Two Columns */}
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="grid grid-cols-2 gap-x-4 gap-y-3 mb-6"
                initial={{ opacity: 0, y: 10 }}
                transition={{ delay: 0.5 }}
              >
                {/* Left Column */}
                <div className="flex items-center space-x-2">
                  <i className="fas fa-check-circle text-green-400 flex-shrink-0 text-sm" />
                  <span className="text-gray-300 text-xs">
                    Create & Manage Playlists
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="fas fa-check-circle text-green-400 flex-shrink-0 text-sm" />
                  <span className="text-gray-300 text-xs">
                    Unlocks Premium App Features
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="fas fa-check-circle text-green-400 flex-shrink-0 text-sm" />
                  <span className="text-gray-300 text-xs">
                    Manage Your Content
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="fas fa-check-circle text-green-400 flex-shrink-0 text-sm" />
                  <span className="text-gray-300 text-xs">
                    Fast Login w/ LunaKeys&trade;
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="fas fa-check-circle text-green-400 flex-shrink-0 text-sm" />
                  <span className="text-gray-300 text-xs">
                    Access To Support
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <i className="fas fa-check-circle text-green-400 flex-shrink-0 text-sm" />
                  <span className="text-gray-300 text-xs">
                    Custom User Profiles
                  </span>
                </div>
              </motion.div>

              {/* Action Buttons */}
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="space-y-3"
                initial={{ opacity: 0, y: 10 }}
                transition={{ delay: 0.6 }}
              >
                <button
                  className="w-full py-3 px-6 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-lg shadow-blue-500/25"
                  style={{ fontFamily: "Open Sans, sans-serif" }}
                  onClick={() => setShowConfirmationModal(false)}
                >
                  Got it, thanks!
                </button>
                <p className="text-center text-gray-400 text-xs">
                  Need help?{" "}
                  <a
                    className="text-blue-400 hover:text-blue-300 transition-colors"
                    href="mailto:support@lunova.app"
                  >
                    Contact support
                  </a>
                </p>
              </motion.div>
            </div>

            {/* Close Button */}
            <button
              aria-label="Close modal"
              className="absolute top-4 right-4 z-10 text-gray-400 hover:text-white transition-all duration-200 p-2 hover:bg-white/10 rounded-lg cursor-pointer"
              type="button"
              onClick={() => setShowConfirmationModal(false)}
            >
              <i className="fas fa-times text-lg pointer-events-none" />
            </button>

            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-400/20 to-transparent rounded-full blur-2xl" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-blue-400/20 to-transparent rounded-full blur-2xl" />
          </motion.div>
        </div>
      )}

      {/* Registration Confirmation Modal */}
      {showRegistrationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            animate={{ opacity: 1 }}
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            onClick={() => setShowRegistrationModal(false)}
          />

          {/* Modal */}
          <motion.div
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden border border-gray-700/50"
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            <div className="absolute inset-0 flex items-center justify-center opacity-10">
              <Lottie
                animationData={verifyEmail}
                autoplay={true}
                loop={true}
                style={{ width: "70%", height: "70%" }}
              />
            </div>

            {/* Modal Content */}
            <div className="relative p-8">
              {/* Success Checkmark */}
              <motion.div
                animate={{ scale: 1 }}
                className="mx-auto w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mb-6 shadow-lg shadow-green-500/25"
                initial={{ scale: 0 }}
                transition={{ delay: 0.1, type: "spring", damping: 15 }}
              >
                <i className="fas fa-check text-white text-3xl" />
              </motion.div>

              {/* Title */}
              <motion.h2
                animate={{ opacity: 1, y: 0 }}
                className="text-3xl font-bold text-white text-center mb-3"
                initial={{ opacity: 0, y: 10 }}
                style={{ fontFamily: "Open Sans, sans-serif" }}
                transition={{ delay: 0.2 }}
              >
                Welcome to Lunova Player!
              </motion.h2>

              {/* Subscription Success Message */}
              <motion.p
                animate={{ opacity: 1, y: 0 }}
                className="text-gray-300 text-center mb-6"
                initial={{ opacity: 0, y: 10 }}
                style={{ fontFamily: "Open Sans, sans-serif" }}
                transition={{ delay: 0.3 }}
              >
                Thank you for registering!
              </motion.p>

              {/* Email Verification Notice */}
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="bg-blue-500/10 border border-blue-400/30 rounded-xl p-4 mb-6"
                initial={{ opacity: 0, y: 10 }}
                transition={{ delay: 0.4 }}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <i className="fas fa-envelope text-blue-400 text-xl" />
                  </div>
                  <div>
                    <h3
                      className="text-white font-semibold mb-1"
                      style={{ fontFamily: "Open Sans, sans-serif" }}
                    >
                      Please Verify Your Email
                    </h3>
                    <p
                      className="text-gray-300 text-sm"
                      style={{ fontFamily: "Open Sans, sans-serif" }}
                    >
                      Your registration is complete! Your account has been
                      successfully created. Please verify your email address to
                      activate your account and start managing your content.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Action Buttons */}
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="space-y-3"
                initial={{ opacity: 0, y: 10 }}
                transition={{ delay: 0.6 }}
              >
                <button
                  className="w-full py-3 px-6 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-lg shadow-blue-500/25"
                  style={{ fontFamily: "Open Sans, sans-serif" }}
                  onClick={() => setShowRegistrationModal(false)}
                >
                  Got it, thanks!
                </button>
                <p className="text-center text-gray-400 text-xs">
                  Need help?{" "}
                  <a
                    className="text-blue-400 hover:text-blue-300 transition-colors"
                    href="mailto:support@lunova.app"
                  >
                    Contact support
                  </a>
                </p>
              </motion.div>
            </div>

            {/* Close Button */}
            <button
              aria-label="Close modal"
              className="absolute top-4 right-4 z-10 text-gray-400 hover:text-white transition-all duration-200 p-2 hover:bg-white/10 rounded-lg cursor-pointer"
              type="button"
              onClick={() => setShowRegistrationModal(false)}
            >
              <i className="fas fa-times text-lg pointer-events-none" />
            </button>

            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-400/20 to-transparent rounded-full blur-2xl" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-blue-400/20 to-transparent rounded-full blur-2xl" />
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Login;
